import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, FormGroup, Grid, IconButton, Stack, TextField, Typography } from "@mui/material";
import { blue, grey } from "@mui/material/colors";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import { useMemo, useState } from "react";
import DashedBreakline from "../../../components/Shared/DashedBreakline";
import { joinTeam, resolveInvitationToken } from "../../../services/team";
import { queryStringParse, setToken } from "../../../utils/functions";

import { vestResolver } from "@hookform/resolvers/vest";
import PasswordIcon from '@mui/icons-material/Password';
import { Controller, useForm } from "react-hook-form";
import validators from "../Register/RegisterIndividualForm/validators";
import { useMutation } from "react-query";

export default function Invitation() {
    const navigate = useNavigate();
    const location = useLocation();
    const { token } = queryStringParse(location?.search);

    const { data } = resolveInvitationToken(token);
    const tokenInvalid = useMemo(() => typeof data?.member?.invite_token === "undefined" || data?.member?.invite_token === null, [data]);

    const { member } = data || {};

    const [type, setType] = useState<string>('password');
    const [accepted, setAccepted] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>("");

    const { mutateAsync, isLoading } = useMutation(joinTeam);

    const {
        control,
        formState: { errors },
        handleSubmit,
    } = useForm({
        defaultValues: {
            email: data?.member?.email,
            password: "",
            password_confirmation: "",
            type: 'individual'
        },
        resolver: vestResolver(validators)
    });

    const handleAcceptInvitation = async (data: any) => {
        setErrorMessage(null);

        data.type = 'individual'
        data.token = token
        data.member = member.id

        const result = await mutateAsync(data);
        console.log(result)

        if (result?.errors) return setErrorMessage(result?.errors?.[0]?.message);

        const { access_token, exp, uuid } = result?.data || {};

        if (access_token && exp && uuid) {
            return [
                setToken(access_token, exp, uuid),
                navigate("/")
            ]
        }
    };

    return (
        <Box sx={{ bgcolor: "#F8F8F8", height: "100vh" }}>
            <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="center"
                sx={{ height: "inherit" }}
            >
                <Grid item md={3} xs={12} sx={{ p: 2 }}>
                    <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
                        <img src="/assets/icon.png" width={50} height={50} />
                    </Box>
                    <Box sx={{ mb: 2, p: 5, borderRadius: 2, bgcolor: "white" }}>
                        <Typography fontWeight={600} sx={{ mb: 1, color: grey[900] }}>Teammates Invitation</Typography>
                        <Typography fontSize={14} sx={{ color: grey[900] }}>You've been invited to a team {data?.team?.team_name && (<b>{data?.team?.team_name}</b>)} on the VNETWORK JSC partner console.</Typography>
                        {!tokenInvalid ? (
                            <Box component="form" onSubmit={handleSubmit(handleAcceptInvitation)} sx={{ mt: 3, p: 2, bgcolor: grey[50] }}>
                                {errorMessage && (
                                    <Alert severity="error" sx={{ mb: 4, fontSize: 14 }}>{errorMessage}</Alert>
                                )}
                                <Typography fontSize={14} sx={{ mb: 2, color: grey[900] }}>Before joining, please take a moment to review the additional steps required:</Typography>
                                <Box sx={{ mb: 2 }}>
                                    <Typography fontSize={14} fontWeight={600} sx={{ mb: 2, color: grey[900] }}>• Set your authentication credential</Typography>
                                    <Controller
                                        control={control}
                                        name="email"
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                disabled
                                                InputLabelProps={{ shrink: true, sx: { fontSize: "13px" } }}
                                                inputProps={{
                                                    readOnly: true,
                                                    sx: { fontSize: 13, bgcolor: "white", borderRadius: 0 },
                                                }}
                                                sx={{
                                                    mb: 1,
                                                    ".MuiInputBase-root": { borderRadius: 0 },
                                                    ".MuiInputLabel-shrink": { transform: "translate(14px, -9px)" },
                                                }}
                                                fullWidth
                                                type="text"
                                                size="small"
                                                label="Email (*)"
                                                error={!!errors.email}
                                                helperText={errors.email?.message as string}
                                            />
                                        )}
                                    />
                                    <Stack direction="row" sx={{ position: 'relative' }}>
                                        <Controller
                                            control={control}
                                            name="password"
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    InputLabelProps={{ shrink: true, sx: { fontSize: "13px" } }}
                                                    inputProps={{
                                                        autoComplete: 'new-password',
                                                        sx: { fontSize: 13, bgcolor: "white", borderRadius: 0 },
                                                    }}
                                                    sx={{
                                                        mt: 1,
                                                        ".MuiInputBase-root": { borderRadius: 0 },
                                                        ".MuiInputLabel-shrink": { transform: "translate(14px, -9px)" },
                                                    }}
                                                    fullWidth
                                                    type={type}
                                                    size="small"
                                                    label="Password (*)"
                                                    error={!!errors?.password}
                                                    helperText={errors?.password?.message}
                                                />
                                            )}
                                        />
                                        <IconButton aria-label="show" sx={{ position: 'absolute', top: 8, right: 0 }} onClick={() => setType(type === 'text' ? 'password' : 'text')}>
                                            <PasswordIcon sx={{ fontSize: 20 }} />
                                        </IconButton>
                                    </Stack>
                                    <Controller
                                        control={control}
                                        name="password_confirmation"
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                InputLabelProps={{ shrink: true, sx: { fontSize: "13px" } }}
                                                inputProps={{
                                                    autoComplete: 'new-password',
                                                    sx: { fontSize: 13, bgcolor: "white", borderRadius: 0 },
                                                }}
                                                sx={{
                                                    mt: 2,
                                                    ".MuiInputBase-root": { borderRadius: 0 },
                                                    ".MuiInputLabel-shrink": { transform: "translate(14px, -9px)" },
                                                }}
                                                fullWidth
                                                type={type}
                                                size="small"
                                                label="Password Confirmation (*)"
                                                error={!!errors?.password_confirmation}
                                                helperText={errors?.password_confirmation?.message}
                                            />
                                        )}
                                    />
                                </Box>
                                <Box>
                                    <Typography fontSize={14} fontWeight={600} sx={{ mb: 0.5, color: grey[900] }}>• Agree our Terms of Service</Typography>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={<Checkbox onChange={(event) => setAccepted(event.target?.checked)} />}
                                            componentsProps={{ typography: { fontSize: 14 } }}
                                            label={
                                                <Typography sx={{ fontSize: 13 }}>By clicking, you acknowledge and agree to the VNETWORK JSC <Typography component={NavLink} fontSize={14} fontWeight={500} target="_blank" to="https://www.vnetwork.vn/en-US/terms-of-service/" sx={{ color: `${blue[900]} ! important` }}>Terms of Service</Typography>.</Typography>
                                            }
                                        />
                                    </FormGroup>
                                </Box>
                                <Button
                                    disabled={isLoading || !accepted}
                                    type="submit"
                                    sx={{ mb: 1, mt: 3 }}
                                    fullWidth
                                    variant="outlined"
                                >
                                    {isLoading ? (
                                        <CircularProgress size={24} sx={{ color: grey[900] }} />
                                    ) : (
                                        <Typography fontSize={14} fontWeight={700} sx={{ textTransform: 'capitalize' }}>Next</Typography>
                                    )}
                                </Button>
                            </Box>
                        ) : (
                            <>
                                <Box sx={{ mt: 3 }}>
                                    <Alert severity="warning">Your invitation may expires or invalid. Please contact our support team for more information.</Alert>
                                </Box>
                            </>
                        )}
                        <DashedBreakline text="Already have account?" />
                        <Button component={NavLink} to="/auth/login" fullWidth sx={{ bgcolor: grey[50], color: 'black ! important' }}>
                            <Typography fontSize={14} fontWeight={700} sx={{ textTransform: 'capitalize' }}>
                                Skip to Login
                            </Typography>
                        </Button>
                    </Box>
                    <Typography fontSize={14} color="#9b9b9b">© 2013 VNETWORK JSC. All Rights Reserved</Typography>
                </Grid>
            </Grid>
        </Box>
    );
}
